// One-finger gestures on the viewer's picture at fit zoom: swiping left or right steps to the next
// or previous photo, up archives it and down closes the viewer. Also tells taps and double taps apart.

import {Point, Size} from './zoomPan';

export type SwipeAction = 'next' | 'previous' | 'up' | 'down';
export type SwipeAxis = 'x' | 'y';

/** How far a finger must move before the drag follows one axis (and stops being a tap). */
export const DRAG_SLOP_PX = 10;
/** The share of the stage's width (x) or height (y) a slow swipe must cover. */
const DISTANCE_THRESHOLD: Record<SwipeAxis, number> = {x: 0.25, y: 0.2};
/** A flick this fast (px/ms) counts even when short… */
const FLICK_VELOCITY = 0.5;
/** …though not shorter than this. */
const FLICK_MIN_PX = 40;
const TAP_MAX_MS = 300;
const DOUBLE_TAP_MAX_MS = 300;
const DOUBLE_TAP_MAX_PX = 30;

export interface Tap {
    point: Point;
    time: number;
}

/** The axis a drag of (`dx`, `dy`) follows; `null` while it is still within the slop. */
export function swipeAxis(dx: number, dy: number): SwipeAxis | null {
    if (Math.hypot(dx, dy) < DRAG_SLOP_PX) return null;
    return Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
}

/** What a finished swipe along `axis` asks for, if it went far or fast enough. */
export function classifySwipe(axis: SwipeAxis, dx: number, dy: number, durationMs: number, stage: Size): SwipeAction | null {
    const distance = axis === 'x' ? dx : dy;
    const length = axis === 'x' ? stage.width : stage.height;
    const far = Math.abs(distance) >= length * DISTANCE_THRESHOLD[axis];
    const flick = Math.abs(distance) >= FLICK_MIN_PX && Math.abs(distance) / Math.max(durationMs, 1) >= FLICK_VELOCITY;
    if (!far && !flick) return null;
    if (axis === 'x') return distance < 0 ? 'next' : 'previous';
    return distance < 0 ? 'up' : 'down';
}

/** Whether a press whose finger (or mouse) strayed at most `distance` and that lasted `durationMs` was a tap. */
export function isTap(distance: number, durationMs: number): boolean {
    return distance < DRAG_SLOP_PX && durationMs <= TAP_MAX_MS;
}

/** Whether `tap` completes a double tap begun by `previous`. */
export function isDoubleTap(previous: Tap | null, tap: Tap): boolean {
    return !!previous
        && tap.time - previous.time <= DOUBLE_TAP_MAX_MS
        && Math.hypot(tap.point.x - previous.point.x, tap.point.y - previous.point.y) <= DOUBLE_TAP_MAX_PX;
}
